import { useEffect, useRef } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

/** How long the search must stay unchanged before it counts as a search rather than typing. */
export const GAPS_SEARCH_SETTLE_MS = 1000;

type Context = {
	departmentId: string;
	/** The tab searched (staff by name, or call-ins by name). */
	tab: string;
	/** What the search found on that tab, once settled. */
	results: number;
	/**
	 * Whether `results` is the tab's real answer: false while its list loads or has failed, and on a tab that takes no
	 * search. A term that settles then is held, not dropped, and sent once the count is real.
	 */
	isReady: boolean;
};

/**
 * Track what the user searched for once they stop typing — one `gaps_search` per settled term, never per keystroke.
 *
 * The classic home tracked a search when a name was picked from its autocomplete (`mobile_staffing_search`); the gaps
 * search filters as you type and has no pick, so "settled" is the moment the term has stood still for
 * `GAPS_SEARCH_SETTLE_MS`. It carries the same `search_term` so the two can be read side by side. A term is tracked
 * once until it changes: tab, results and `track`'s identity moving under a settled term are not a new search, and
 * clearing the field sends nothing but lets the same term count again next time.
 *
 * Pass the term the results were filtered by (the deferred one), so the term sent and its count are one value.
 */
export const useTrackSettledSearch = (searchTerm: string, { departmentId, tab, results, isReady }: Context): void => {
	const { track } = useAnalytics();
	const settled = useDebouncedValue(searchTerm.trim(), GAPS_SEARCH_SETTLE_MS);
	const lastSettledRef = useRef("");

	useEffect(() => {
		if (settled === lastSettledRef.current) return;
		if (!isReady) return;
		lastSettledRef.current = settled;
		if (!settled) return;
		track("gaps_search", { department_id: departmentId, search_term: settled, tab, results });
	}, [settled, isReady, track, departmentId, tab, results]);
};
